'use client';

import React, { useEffect, useState } from 'react';
import { LayoutContainer } from '../../atoms/LayoutContainer';
import { List } from '../../atoms/List';
import { ListItem } from '../../atoms/ListItem';
import { Link } from '../../atoms/Link';
import './SectionNav.css';

export interface SectionNavItemData {
  label: string;
  /** Target path. Omitted when the item is a pure dropdown trigger. */
  href?: string;
  /** Sub-links. Present ⇒ this item is a dropdown (legacy `li.dropdown`). */
  children?: SectionNavItemData[];
}

/**
 * Optional "View Basket" button on the right of the bar (legacy
 * `.subpages_right .view-basket`). Opt-in per section — only passed where a
 * basket exists (e.g. the domain-names search page). Shown below 1024 only (the
 * page's own basket UI takes over above that). The consumer owns the count and
 * what the click does (open a modal, go to checkout, …).
 */
export interface SectionNavBasket {
  /** Item count; the label gains "(N)" when above zero. */
  count: number;
  /** Invoked when the button is clicked. */
  onView: () => void;
  /** Base label (default "View Basket"). */
  label?: string;
}

/**
 * Optional labelled two-state toggle on the right of the bar (legacy
 * `.subpages .switch-wrap` — e.g. the web-hosting "Prices | Monthly ⇄ Annual"
 * duration switch). Opt-in per section, occupying the same right slot as the
 * basket button but shown at ALL widths (it's a primary control, not a
 * below-1024 hand-off). The consumer owns the state and what flipping it does.
 */
export interface SectionNavToggle {
  /** Label for the "off" (left, unchecked) option, e.g. "Monthly". */
  offLabel: string;
  /** Label for the "on" (right, checked) option, e.g. "Annual". */
  onLabel: string;
  /** Whether the "on"/right option is currently selected. */
  on: boolean;
  /** Called with the next value when the switch is flipped. */
  onToggle: (on: boolean) => void;
  /** Optional text before the options (legacy "Prices | "). */
  prefix?: string;
  /** Accessible name for the switch control. */
  ariaLabel: string;
}

export interface SectionNavProps {
  /** The section's links (e.g. the domain-names sub-pages). */
  items: SectionNavItemData[];
  /** Optional View Basket button on the right of the bar (below 1024 only). */
  basket?: SectionNavBasket;
  /** Optional labelled toggle on the right of the bar (all widths). */
  toggle?: SectionNavToggle;
  /**
   * Current path (e.g. `/domain-names/co-uk`). The item — or dropdown child —
   * whose href is the longest exact-or-prefix match is marked active; a matching
   * child also marks its parent active. Omitted ⇒ none active.
   */
  currentPath?: string;
  /** Accessible name for the landmark (e.g. "Domain names"). */
  ariaLabel?: string;
  className?: string;
}

// The most specific matching href across every item and dropdown child: exact
// path, else the longest href that's a path-segment prefix of the current path.
const activeHrefFor = (items: SectionNavItemData[], currentPath?: string): string | undefined => {
  if (!currentPath) return undefined;
  const hrefs = items.flatMap((item) => [item, ...(item.children ?? [])]).map((item) => item.href);
  return hrefs
    .filter((href): href is string => href !== undefined)
    .filter((href) => currentPath === href || currentPath.startsWith(`${href}/`))
    .sort((a, b) => b.length - a.length)[0];
};

// Section sub-navigation (legacy `.subpages` green bar): a horizontal link bar
// below the MainNav on a section's pages. An item with `children` is a dropdown
// (revealed on hover/focus on desktop; nested in the panel on mobile). Below 768
// the whole bar collapses to a hamburger. Active link carries a white underline;
// a matching dropdown child also lights its parent. Client component for the
// mobile toggle (mirrors MainNav).
export const SectionNav: React.FC<SectionNavProps> = ({
  items,
  basket,
  toggle,
  currentPath,
  ariaLabel = 'Section',
  className = '',
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  // The open mobile menu is a full-screen overlay; lock background scroll so the
  // page can't scroll behind it (it only renders as an overlay below 768).
  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  // Crossing back to desktop hides the toggle, so close the menu (releasing the
  // scroll lock) rather than stranding a desktop page with locked scroll.
  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 768px)');
    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) setMobileOpen(false);
    };
    desktop.addEventListener('change', handleChange);
    return () => desktop.removeEventListener('change', handleChange);
  }, []);

  const activeHref = activeHrefFor(items, currentPath);

  const isLinkActive = (href?: string): boolean => href !== undefined && href === activeHref;

  return (
    <nav
      id="hi-ui-section-nav"
      aria-label={ariaLabel}
      className={`hi-ui hi-ui-section-nav ${className}`.trim()}
    >
      <LayoutContainer>
        <div className="hi-ui-section-nav__bar">
          <button
            type="button"
            className="hi-ui-section-nav__toggle"
            aria-expanded={mobileOpen}
            aria-controls="hi-ui-section-nav-menu"
            aria-label="Toggle section menu"
            onClick={() => setMobileOpen((open) => !open)}
          >
            {/* Three square-ended bars (legacy `.navicon`); CSS rotates them into
                an X when the button is aria-expanded. */}
            <span className="hi-ui-section-nav__hamburger" aria-hidden="true" />
          </button>
          <List
            variant="unordered"
            listStyle="none"
            id="hi-ui-section-nav-menu"
            className={`hi-ui-section-nav__menu ${mobileOpen ? 'hi-ui-section-nav__menu--open' : ''}`}
          >
            {items.map((item) => {
              const children = item.children ?? [];
              const isDropdown = children.length > 0;
              const parentActive =
                isLinkActive(item.href) || children.some((child) => isLinkActive(child.href));

              return (
                <ListItem
                  key={item.label}
                  className={`hi-ui-section-nav__item ${isDropdown ? 'hi-ui-section-nav__item--dropdown' : ''}`}
                >
                  {isDropdown ? (
                    <button
                      type="button"
                      aria-haspopup="true"
                      className={`hi-ui-section-nav__link hi-ui-section-nav__trigger ${parentActive ? 'hi-ui-section-nav__link--active' : ''}`}
                    >
                      {item.label}
                    </button>
                  ) : (
                    <Link
                      // A leaf item always has an href (only dropdown triggers omit it).
                      href={item.href as string}
                      noUnderline
                      aria-current={parentActive ? 'page' : undefined}
                      className={`hi-ui-section-nav__link ${parentActive ? 'hi-ui-section-nav__link--active' : ''}`}
                    >
                      {item.label}
                    </Link>
                  )}

                  {isDropdown && (
                    <List
                      variant="unordered"
                      listStyle="none"
                      className="hi-ui-section-nav__dropdown"
                    >
                      {children.map((child) => (
                        <ListItem key={child.href} className="hi-ui-section-nav__dropdown-item">
                          <Link
                            href={child.href as string}
                            noUnderline
                            aria-current={isLinkActive(child.href) ? 'page' : undefined}
                            className={`hi-ui-section-nav__dropdown-link ${isLinkActive(child.href) ? 'hi-ui-section-nav__dropdown-link--active' : ''}`}
                          >
                            {child.label}
                          </Link>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </ListItem>
              );
            })}
          </List>

          {basket &&
            (() => {
              const label = basket.label ?? 'View Basket';
              return (
                <button type="button" className="hi-ui-section-nav__basket" onClick={basket.onView}>
                  {basket.count > 0 ? `${label} (${basket.count})` : label}
                </button>
              );
            })()}

          {toggle && (
            <div className="hi-ui-section-nav__switch-wrap">
              {toggle.prefix && (
                <span className="hi-ui-section-nav__switch-title">{toggle.prefix}</span>
              )}
              <span className="hi-ui-section-nav__switch-option">{toggle.offLabel}</span>
              <button
                type="button"
                role="switch"
                aria-checked={toggle.on}
                aria-label={toggle.ariaLabel}
                className={`hi-ui-section-nav__switch ${toggle.on ? 'hi-ui-section-nav__switch--on' : ''}`}
                onClick={() => toggle.onToggle(!toggle.on)}
              >
                <span className="hi-ui-section-nav__switch-knob" aria-hidden="true" />
              </button>
              <span className="hi-ui-section-nav__switch-option">{toggle.onLabel}</span>
            </div>
          )}
        </div>
      </LayoutContainer>
    </nav>
  );
};
